/**
 * GTM Custom Event Tracking for Happy Day
 * Tracks 49 events across ecommerce funnel, navigation, delivery popup, filters, checkout, and engagement.
 * All events push to dataLayer — GTM Consent Mode v2 controls which tags fire.
 */
class gTagEvents {
    constructor() {
        this.delegateBody = new Delegate(document.body);

        this.bindEvents();
        this.interceptCartApi();

        // Wait for consent before firing page-load events
        if (window.__gtmConsentReady) {
            this.handleIntersects();
        } else {
            document.addEventListener('gtm:consent-ready', () => this.handleIntersects(), {once: true});
            // Fallback: init after 3s even if consent never resolves
            setTimeout(() => { if (!this._intersectsInitialized) this.handleIntersects(); }, 3000);
        }
    }

    // ========================================
    // EVENT BINDING
    // ========================================

    bindEvents() {
        // Ecommerce events
        this.delegateBody.on("click", "button[name='checkout']", this.handleCheckout.bind(this));
        this.delegateBody.on("click", '[data-action="ga-send-select"]', this.handleSelectItem.bind(this));
        this.delegateBody.on('variant:changed', this.handleVariantChange.bind(this));

        // Navigation events — Desktop menu (use capturing to run before global data-action handler)
        document.addEventListener("click", (e) => {
            const menuItem = e.target.closest(".header__menu-item");
            if (menuItem) this.handleMenuClick(e);
        }, true);
        this.delegateBody.on("click", ".desktop-menu__item a", this.handleSubmenuClick.bind(this));

        // Navigation events — Mobile menu (items can be <a> or <div> depending on children)
        this.delegateBody.on("click", ".mob-menu__first-lvl .mob-menu__item", this.handleMobileMenuClick.bind(this));
        this.delegateBody.on("click", ".mob-menu__next-lvl .mob-menu__item", this.handleMobileMenuClick.bind(this));

        // Navigation events — Header icons
        this.delegateBody.on("click", ".header-cart-btn", this.handleHeaderIconClick.bind(this, "cart"));
        this.delegateBody.on("click", ".header-acc-btn", this.handleHeaderIconClick.bind(this, "account"));
        this.delegateBody.on("click", "favorites-header a", this.handleHeaderIconClick.bind(this, "favorites"));
        this.delegateBody.on("click", "[data-action='open-search']", this.handleHeaderIconClick.bind(this, "search"));
        this.delegateBody.on("click", ".header__menu-customer-btn", this.handleHeaderIconClick.bind(this, "for_business"));

        // Navigation events — Logo
        this.delegateBody.on("click", ".header-logo-wrapper a", this.handleLogoClick.bind(this));

        // Navigation events — Footer links
        this.delegateBody.on("click", ".Dropdown--Content a", this.handleFooterLinkClick.bind(this));
        this.delegateBody.on("click", ".footer__bottom-link", this.handleFooterLinkClick.bind(this));

        // Navigation events — Breadcrumbs
        this.delegateBody.on("click", ".breadcrumbs__link", this.handleBreadcrumbClick.bind(this));

        // Navigation events — Announcement bar
        this.delegateBody.on("click", ".ann-bar--content a", this.handleAnnouncementClick.bind(this));

        // Search bar open — fire view_item_list for search recommendations (desktop + mobile)
        // Do NOT match "search-bar" element itself — clicks on product cards inside it would re-trigger
        this.delegateBody.on("click", "[data-action='open-search'], [aria-controls='SearchPopupMobile'], .header__bottom-bar-search-input-btn, .searchInput", this.handleSearchBarOpen.bind(this));

        // Search location click
        this.delegateBody.on("click", ".search-wrapper__location-card", this.handleSearchLocationClick.bind(this));

        // CTA button clicks (hero/banner sections)
        this.delegateBody.on("click", "banner-hero a.Button, .media-slideshow__wrapper a.Button, .group-text-button__block-wrapper a.Button", this.handleCtaClick.bind(this));

        // Newsletter form submit
        this.delegateBody.on("submit", "#footerForm, .newsletter-form, [data-role='footer-newsletter-form']", this.handleNewsletterSignup.bind(this));

        // FAQ accordion (scoped to faq-section to avoid matching other dropdowns)
        this.delegateBody.on("click", "faq-section .faq-question, faq-section .FAQbutton", this.handleFaqClick.bind(this));

        // Notify me form submit
        this.delegateBody.on("submit", ".notify-me-popup form, [data-action='notify-me-submit']", this.handleNotifyMeSubmit.bind(this));

        // Image gallery zoom (PhotoSwipe)
        this.delegateBody.on("click", "[data-action='open-product-zoom'], .swiper-zoom-target, .ProductSliderCell--Zoom", this.handleImageGalleryInteraction.bind(this));

        // Search event — wait for consent before firing
        if (window.__gtmConsentReady) {
            this.fireSearchEvent();
        } else {
            document.addEventListener('gtm:consent-ready', () => this.fireSearchEvent(), {once: true});
        }

        // Predictive search results — track when results appear
        this.observePredictiveSearch();
    }

    // ========================================
    // INTERSECTION OBSERVER — view_item_list for carousels
    // ========================================

    handleIntersects() {
        if (this._intersectsInitialized) return;
        this._intersectsInitialized = true;
        // Scan for [data-gtag-intersection] elements and observe them.
        this.scanAndObserve();
        this.watchForAsyncSections();
    }

    scanAndObserve() {
        const elements = document.querySelectorAll('[data-gtag-intersection][data-observed="false"]');
        elements.forEach((el) => {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    observer.unobserve(entry.target);

                    try {
                        const data = JSON.parse(
                            entry.target.querySelector('script[type="application/ld+json"]').textContent
                        );
                        if (data && window.dataLayer) {
                            dataLayer.push({ ecommerce: null });
                            dataLayer.push(data);
                        }
                    } catch (e) {}
                });
            }, { threshold: 0.1 });

            observer.observe(el);
            el.dataset.observed = "true";
        });
    }

    watchForAsyncSections() {
        // Product recommendations load async via Shopify API.
        // Watch their containers for new content (lightweight, scoped observers).
        const containers = document.querySelectorAll(
            'product-recommendations, .product-recommendations, [data-section-type="product-recommendations"], .pc__swiper-main-wrapper'
        );

        containers.forEach(container => {
            const observer = new MutationObserver(() => {
                const unobserved = container.querySelectorAll('[data-gtag-intersection][data-observed="false"]');
                if (unobserved.length > 0) {
                    this.scanAndObserve();
                    observer.disconnect(); // Found them, stop watching
                }
            });
            observer.observe(container, { childList: true, subtree: true });
        });
    }

    // ========================================
    // VARIANT CHANGE — custom_variant_changed
    // ========================================

    handleVariantChange(event) {
        const params = new URLSearchParams(window.location.search);
        const variantIdParam = params.get("variant");

        if (!variantIdParam) return;
        const currentVariantID = Number(variantIdParam);

        const variants = window.ShopifyAnalytics?.meta?.product?.variants;
        if (!variants) return;
        let currentVariant = null;

        variants.forEach(variant => {
            if (variant.id === currentVariantID) currentVariant = variant;
        });

        const dataElem = document.querySelector('.main-product__gtm-script script[type="application/ld+json"]');
        if (!currentVariant || !dataElem) return;

        try {
            const data = JSON.parse(dataElem.textContent);
            const item = data.ecommerce.items[0];

            item.item_variant = currentVariant.public_title || "Default Title";
            item.item_id = currentVariant.id || "";
            item.price = currentVariant.price / 100;
            item.quantity = 1;

            data.ecommerce.value = currentVariant.price / 100;

            if (!window.dataLayer) return;

            dataLayer.push({ ecommerce: null });
            dataLayer.push(data);

            // Also push custom_variant_changed event
            dataLayer.push({
                event: "custom_variant_changed",
                product_name: window.ShopifyAnalytics?.meta?.product?.title || "",
                variant_id: currentVariant.id,
                variant_title: currentVariant.public_title || "Default Title",
                price: currentVariant.price / 100
            });
        } catch (e) {}
    }

    // ========================================
    // CART API INTERCEPTION — add/remove/clear
    // ========================================

    interceptCartApi() {
        const originalFetch = window.fetch;
        const _this = this;

        window.fetch = async function (...args) {
            const [resource, request] = args;
            let action;
            try {
                const url = new URL(typeof resource === 'string' ? resource : resource?.url || resource?.href, window.location.href);
                const method = String(request?.method || resource?.method || 'GET').toUpperCase();
                if (url.origin === new URL(window.location.href).origin && method === 'POST') {
                    action = url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?$/)?.[1];
                }
            } catch (_) { /* Not a cart URL; leave fetch behavior unchanged. */ }
            if (!action) return originalFetch.apply(this, args);

            let trackingRequest = request;
            let previousCart;
            try {
                // Read a clone so Request bodies remain available to the actual mutation.
                if (!request?.body && typeof resource?.clone === 'function') {
                    const clone = resource.clone();
                    const formBody = /multipart\/form-data|application\/x-www-form-urlencoded/i.test(clone.headers.get('content-type') || '');
                    trackingRequest = { ...request, body: await (formBody ? clone.formData() : clone.text()) };
                }
                if (window.dataLayer && (action === 'clear'
                    || (action === 'update' && _this.parseCartRequestBody(trackingRequest?.body)?.updates))) {
                    previousCart = await _this.getCart();
                }
            } catch (error) {
                console.warn('[GTM] Could not prepare cart tracking', error);
            }

            // The mutation happens exactly once. Analytics errors must never retry a cart write.
            const response = await originalFetch.apply(this, args);
            if (!response.ok || !window.dataLayer) return response;
            try {
                if (action === 'add') {
                    await _this.cartAdd(await response.clone().json(), trackingRequest);
                } else if (action === 'update' && previousCart) {
                    await _this.cartUpdate(trackingRequest?.body, previousCart, await response.clone().json());
                } else if (action === 'clear' && previousCart) {
                    await _this.cartClear(previousCart);
                } else if (action === 'change') {
                    await _this.cartChange(await response.clone().json());
                }
            } catch (error) {
                console.warn('[GTM] Cart tracking failed after a successful cart request', error);
            }
            return response;
        };
    }

    async cartAdd(cart, request) {
        let cartItems = cart.items ? cart.items : [cart];
        // Filter out giftbox/bow accessories — only track the main product
        cartItems = cartItems.filter(item => {
            const itemType = item.properties?.["_item_type"];
            return !itemType || itemType === 'main';
        });
        if (cartItems.length === 0) return;
        cartItems = this.getDataFromRequest(cartItems, request);
        this.productAdded(cartItems);
    }

    async cartUpdate(body, previousCart, confirmedCart) {
        if (!body) return;

        const updates = this.parseCartRequestBody(body)?.updates;
        // The cart delivery experience posts attribute-only updates to /cart/update.js, which
        // carry no line quantities — there is nothing to report as added or removed.
        if (!updates || typeof updates !== 'object') return;

        const oldCart = previousCart || await this.getCart();
        const cartItems = oldCart.items, itemsAdded = [], itemsRemoved = [];
        if (confirmedCart && !Array.isArray(confirmedCart.items)) return;
        const compared = new Set();

        cartItems.forEach((item, index) => {
            const requestedQuantity = Array.isArray(updates) ? updates[index] : updates[item.key] ?? updates[item.variant_id];
            if (requestedQuantity !== undefined) {
                let previousQuantity = Number(item.quantity);
                let newQuantity = Number(requestedQuantity);
                if (confirmedCart) {
                    // Discounts can re-key or split a line. Compare the total for the same
                    // variant/properties/plan once, without treating a new key as a removal.
                    const identity = this.getCartItemIdentity(item);
                    if (compared.has(identity)) return;
                    compared.add(identity);
                    const quantityFor = items => items.reduce((sum, line) =>
                        sum + (this.getCartItemIdentity(line) === identity ? Number(line.quantity) : 0), 0);
                    previousQuantity = quantityFor(cartItems);
                    newQuantity = quantityFor(confirmedCart.items);
                }
                if (!Number.isFinite(newQuantity) || newQuantity < 0) return;
                const change = newQuantity - previousQuantity;
                if (!change) return;
                const changedItem = { ...item, quantity: Math.abs(change) };
                change < 0 ? itemsRemoved.push(changedItem) : itemsAdded.push(changedItem);
            }
        });

        this.productAdded(itemsAdded);
        this.productsRemoved(itemsRemoved);
    }

    async cartChange(cart) {
        this.productAdded(cart.items_added);
        this.productsRemoved(cart.items_removed);
    }

    async cartClear(previousCart) {
        const cart = previousCart || await this.getCart();
        this.productsRemoved(cart.items);
    }

    async getCart() {
        try {
            const response = await fetch(window.routes.localeRootUrl + "/cart.js", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            });

            const cart = await response.json();
            if (typeof window.syncKnownCartStateFromCart === 'function') {
                window.syncKnownCartStateFromCart(cart);
            } else if (typeof window.updateKnownCartState === 'function') {
                window.updateKnownCartState(cart);
            } else {
                const itemCount = Number(cart?.item_count || 0);
                const itemLength = Array.isArray(cart?.items) ? cart.items.length : 0;
                const cartStateMeta = typeof window.buildCartStateMeta === 'function'
                    ? window.buildCartStateMeta()
                    : { updated_at: new Date().toISOString(), updated_at_ms: Date.now() };

                window.cart = cart;
                window.__cartState = {
                    isLoaded: true,
                    itemCount,
                    isEmpty: itemCount === 0 && itemLength === 0,
                    ...cartStateMeta
                };
            }
            return cart;
        } catch (error) {
            return { items: [] };
        }
    }

    productAdded(cartItems) {
        cartItems = this.getTrackedCartItems(cartItems);
        if (!cartItems || !cartItems.length) return;

        const customEvent = {
            'event': 'custom_add_to_cart',
            'ecommerce': this.buildEcommerce(cartItems)
        };

        dataLayer.push({ ecommerce: null });
        dataLayer.push(customEvent);
    }

    productsRemoved(cartItems) {
        cartItems = this.getTrackedCartItems(cartItems);
        if (!cartItems || !cartItems.length) return;

        const customEvent = {
            'event': "custom_remove_from_cart",
            'ecommerce': this.buildEcommerce(cartItems)
        };

        dataLayer.push({ ecommerce: null });
        dataLayer.push(customEvent);
    }

    // ========================================
    // CHECKOUT — begin_checkout
    // ========================================

    handleCheckout(event) {
        const button = event.target.closest("button[name='checkout']");
        if (!button) return;

        const script = button.parentElement.querySelector('[type="application/ld+json"]');
        if (!script) return;
        const settings = JSON.parse(script.innerText);

        if (!dataLayer || !settings) return;

        dataLayer.push({ ecommerce: null });
        dataLayer.push(settings);
    }

    // ========================================
    // SELECT ITEM — custom_select_item
    // ========================================

    handleSelectItem(event) {
        let target = event.target.closest('[data-action="ga-send-select"]');
        if (!target || !dataLayer) return;

        const settings = JSON.parse(target.dataset.selectSettings);
        if (!settings) return;

        const customEvent = {
            event: "custom_select_item",
            ecommerce: {
                selected_item_list_id: settings.list_id,
                selected_item_list_name: settings.list_name,
                currency: window.ShopifyAnalytics?.meta?.currency || window.Shopify?.currency?.active || 'SEK',
                items: [settings]
            }
        };

        dataLayer.push({ ecommerce: null });
        dataLayer.push(customEvent);
    }

    // ========================================
    // NAVIGATION EVENTS
    // ========================================

    handleMenuClick(event) {
        const menuItem = event.target.closest(".header__menu-item");
        if (!menuItem) return;

        const link = menuItem.querySelector("a.header-main-links");
        if (!link) return;

        dataLayer.push({
            event: "custom_menu_click",
            link_text: (link.childNodes[0]?.textContent || link.textContent || "").trim(),
            link_url: link.href || "",
            link_handle: menuItem ? menuItem.dataset.handle || "" : "",
            menu_location: "header_desktop",
            has_submenu: menuItem ? menuItem.getAttribute("aria-haspopup") === "true" : false
        });
    }

    handleSubmenuClick(event) {
        const link = event.target.closest("a");
        if (!link) return;

        const dropdown = link.closest("desktop-menu");
        const parentHandle = dropdown ? dropdown.dataset.dropdown || "" : "";
        const isSecondLevel = link.closest(".second-column");

        dataLayer.push({
            event: "custom_submenu_click",
            link_text: (link.textContent || "").trim(),
            link_url: link.href || "",
            parent_menu: parentHandle,
            menu_level: isSecondLevel ? 3 : 2,
            menu_location: "header_desktop_dropdown"
        });
    }

    handleMobileMenuClick(event) {
        const item = event.target.closest(".mob-menu__item");
        if (!item) return;

        const isLink = item.tagName === "A";
        const hasChildren = item.hasAttribute("data-action") && item.dataset.action === "next-lvl";
        const labelEl = item.querySelector(".mob-menu__item-link");

        const lvlContainer = item.closest(".mob-menu__next-lvl");
        let menuLevel = 1;
        if (lvlContainer) {
            const lvlClass = lvlContainer.className;
            if (lvlClass.includes("lvl--third")) menuLevel = 3;
            else if (lvlClass.includes("lvl--second")) menuLevel = 2;
        }

        dataLayer.push({
            event: "custom_mobile_menu_click",
            link_text: labelEl ? (labelEl.textContent || "").trim() : (item.textContent || "").trim(),
            link_url: isLink ? item.href || "" : "",
            menu_level: menuLevel,
            menu_location: "mobile_menu",
            has_children: hasChildren
        });
    }

    handleHeaderIconClick(iconType, event) {
        dataLayer.push({
            event: "custom_header_icon_click",
            icon_type: iconType,
            menu_location: "header"
        });
    }

    handleLogoClick(event) {
        dataLayer.push({
            event: "custom_logo_click",
            menu_location: "header"
        });
    }

    handleFooterLinkClick(event) {
        const link = event.target.closest("a");
        if (!link) return;

        const dropdown = link.closest(".Dropdown");
        const sectionTitle = dropdown ? (dropdown.querySelector(".Dropdown--Button") || {}).textContent : "";

        dataLayer.push({
            event: "custom_footer_link_click",
            link_text: (link.textContent || "").trim(),
            link_url: link.href || "",
            footer_section: (sectionTitle || "").trim(),
            menu_location: "footer"
        });
    }

    handleBreadcrumbClick(event) {
        const link = event.target.closest(".breadcrumbs__link");
        if (!link) return;

        const listItem = link.closest(".breadcrumbs__item");
        const allItems = document.querySelectorAll(".breadcrumbs__item");
        let position = 0;
        allItems.forEach((item, index) => {
            if (item === listItem) position = index + 1;
        });

        dataLayer.push({
            event: "custom_breadcrumb_click",
            link_text: (link.textContent || "").trim(),
            link_url: link.href || "",
            breadcrumb_position: position,
            menu_location: "breadcrumb"
        });
    }

    handleAnnouncementClick(event) {
        const link = event.target.closest("a");
        if (!link) return;

        const slide = link.closest(".swiper-slide");
        const allSlides = document.querySelectorAll(".ann-bar .swiper-slide");
        let slideIndex = 0;
        allSlides.forEach((s, i) => {
            if (s === slide) slideIndex = i;
        });

        dataLayer.push({
            event: "custom_announcement_bar_click",
            link_text: (link.textContent || "").trim(),
            link_url: link.href || "",
            slide_index: slideIndex,
            menu_location: "announcement_bar"
        });
    }

    // ========================================
    // SEARCH LOCATION CLICK
    // ========================================

    handleSearchLocationClick(event) {
        const card = event.target.closest(".search-wrapper__location-card");
        if (!card) return;

        const locationName = card.dataset.location || "";
        const searchInput = document.querySelector(".searchInput");
        const searchTerm = searchInput ? searchInput.value || "" : "";

        dataLayer.push({
            event: "custom_search_location_click",
            location_name: locationName,
            search_term: searchTerm
        });
    }

    // ========================================
    // CTA BUTTON CLICKS
    // ========================================

    handleCtaClick(event) {
        const link = event.target.closest("a");
        if (!link) return;

        const section = link.closest("section, .shopify-section");
        const slide = link.closest(".swiper-slide");
        const allSlides = section ? section.querySelectorAll(".swiper-slide") : [];
        let slideIndex = 0;
        allSlides.forEach((s, i) => {
            if (s === slide) slideIndex = i;
        });

        dataLayer.push({
            event: "custom_cta_click",
            cta_text: (link.textContent || "").trim(),
            cta_url: link.href || "",
            section_type: section ? section.className.split(" ")[0] || "" : "",
            slide_index: slideIndex
        });
    }

    // ========================================
    // NEWSLETTER SIGNUP
    // ========================================

    handleNewsletterSignup(event) {
        const form = event.target.closest("form");
        if (!form) return;

        const isPopup = !!form.closest(".newsletter-popup, .section-newsletter_popup");

        dataLayer.push({
            event: "custom_newsletter_signup",
            signup_location: isPopup ? "popup" : "footer",
            klaviyo: !!window.klaviyoListId
        });
    }

    // ========================================
    // FAQ ACCORDION
    // ========================================

    handleFaqClick(event) {
        const btn = event.target.closest(".faq-question, .FAQbutton");
        if (!btn) return;

        const faqSection = btn.closest("faq-section");
        const activeTab = faqSection ? faqSection.querySelector(".tc-tabs__nav-item.is-active") : null;

        dataLayer.push({
            event: "custom_faq_item_clicked",
            faq_category: activeTab ? (activeTab.textContent || "").trim() : "",
            faq_question: (btn.textContent || "").trim()
        });
    }

    // ========================================
    // NOTIFY ME — custom_notify_me_submitted
    // ========================================

    handleNotifyMeSubmit(event) {
        const form = event.target.closest("form");
        if (!form) return;

        const productComp = document.querySelector("product-component");
        const productName = productComp ? productComp.dataset.title || "" : "";
        const variantId = productComp ? Number(productComp.dataset.firstVariantId) || 0 : 0;

        dataLayer.push({
            event: "custom_notify_me_submitted",
            product_name: productName,
            variant_id: variantId
        });
    }

    // ========================================
    // IMAGE GALLERY — custom_image_gallery_interaction
    // ========================================

    handleImageGalleryInteraction(event) {
        const productComp = document.querySelector("product-component");
        const productName = productComp ? productComp.dataset.title || "" : "";

        const galleryItem = event.target.closest(".pm__gallery-item, .swiper-slide");
        const allItems = document.querySelectorAll(".pm__gallery-item, .pm__content-info .swiper-slide");
        let imageIndex = 0;
        if (galleryItem) {
            allItems.forEach((item, i) => {
                if (item === galleryItem) imageIndex = i;
            });
        }

        dataLayer.push({
            event: "custom_image_gallery_interaction",
            product_name: productName,
            image_index: imageIndex
        });
    }

    // ========================================
    // SEARCH EVENT — search (on page load)
    // ========================================

    fireSearchEvent() {
        // Check if we're on the search page via body class or URL
        const isSearchPage = document.body.classList.contains("template-search") || window.location.pathname.includes("/search");
        if (!isSearchPage) return;

        const params = new URLSearchParams(window.location.search);
        const searchTerm = params.get("q") || "";
        if (!searchTerm) return;

        dataLayer.push({
            event: "search",
            search_term: searchTerm
        });
    }

    // ========================================
    // SEARCH BAR OPEN — view_item_list for recommendations
    // ========================================

    handleSearchBarOpen() {
        if (this._searchRecsTracked) return;
        this._searchRecsTracked = true;

        // Fire view_item_list from the search recommendations swiper (desktop or mobile)
        setTimeout(() => {
            if (!window.dataLayer) return;

            // Find JSON-LD in any search products swiper (desktop or mobile popup)
            const scripts = document.querySelectorAll('.search-wrapper__products-swiper [type="application/ld+json"]');
            for (const script of scripts) {
                try {
                    const data = JSON.parse(script.textContent);
                    if (data?.ecommerce?.items?.length > 0) {
                        dataLayer.push({ ecommerce: null });
                        dataLayer.push(data);
                        return;
                    }
                } catch(e) {}
            }
        }, 500);
    }

    // ========================================
    // PREDICTIVE SEARCH — track results as they load
    // ========================================

    observePredictiveSearch() {
        const containers = document.querySelectorAll('.search-wrapper__predictive-search');
        if (containers.length === 0) return;

        containers.forEach(container => {
            const observer = new MutationObserver(() => {
                const products = container.querySelectorAll('product-card, .product-card, .search-predictive__product-slide');
                if (products.length === 0) return;

                const searchInputs = document.querySelectorAll('.searchInput');
                let searchTerm = '';
                searchInputs.forEach(input => { if (input.value) searchTerm = input.value; });
                if (!searchTerm) return;

                if (container.dataset.lastTrackedTerm === searchTerm) return;
                container.dataset.lastTrackedTerm = searchTerm;

                const items = [];
                const seen = new Set();
                products.forEach((card) => {
                    const productCard = card.closest('product-card') || card.querySelector('product-card') || card;
                    const titleEl = productCard.querySelector('.pc__information-title') || productCard.querySelector('.pc__title');
                    const title = titleEl?.textContent?.trim() || '';
                    if (!title || seen.has(title)) return;
                    seen.add(title);

                    // Extract data from select_item attributes if available
                    const selectEl = productCard.querySelector('[data-select-settings]');
                    let itemData = { item_name: title, index: items.length + 1 };
                    if (selectEl) {
                        try {
                            const settings = JSON.parse(selectEl.dataset.selectSettings);
                            itemData = { ...settings, index: items.length + 1 };
                        } catch(e) {}
                    } else {
                        // Fallback: extract price from DOM
                        const priceEl = productCard.querySelector('.price .price__item--regular, .pc__price');
                        if (priceEl) {
                            const priceText = priceEl.textContent.replace(/[^\d.,]/g, '').replace(',', '.');
                            itemData.price = parseFloat(priceText) || 0;
                        }
                    }
                    items.push(itemData);
                });

                if (window.dataLayer && items.length > 0) {
                    dataLayer.push({ ecommerce: null });
                    dataLayer.push({
                        event: 'custom_view_search_results',
                        ecommerce: {
                            search_term: searchTerm,
                            currency: window.ShopifyAnalytics?.meta?.currency || window.Shopify?.currency?.active || 'SEK',
                            item_list_id: 'predictive_search',
                            item_list_name: 'Predictive search',
                            items: items
                        }
                    });
                }
            });

            observer.observe(container, { childList: true, subtree: true });
        });
    }

    // ========================================
    // HELPERS
    // ========================================

    buildEcommerce(cartItems) {
        const items = [];
        let totalPrice = 0;

        cartItems.forEach((item) => {
            const price = typeof item.price === "number" ? item.price / 100 : Number(item.price);
            const qty = item.quantity || 1;
            totalPrice += price * qty;

            const product = {
                'item_id': item.variant_id || item.id || "",
                'item_name': item.untranslated_product_title || item.title || "",
                'item_variant': item.untranslated_variant_title || "Default Title",
                'price': price,
                'quantity': qty,
                'category': item.properties?.["_gtm-category"] || "",
                'category2': item.properties?.["_gtm-category2"] || "",
                'item_brand': item.properties?.["_gtm-item_brand"] || "",
                'list_id': item.properties?.["_gtm-list_id"] || "",
                'list_name': item.properties?.["_gtm-list_name"] || ""
            };

            items.push(product);
        });

        return {
            'value': totalPrice,
            'currency': window.ShopifyAnalytics?.meta?.currency || window.Shopify?.currency?.active || 'SEK',
            'items': items
        };
    }

    parseCartRequestBody(body) {
        try {
            if (body instanceof FormData || body instanceof URLSearchParams) {
                const parsed = Object.fromEntries(body.entries());
                parsed.properties = {};
                for (const [key, value] of body.entries()) {
                    const property = key.match(/^properties\[(.+)\]$/);
                    if (property) parsed.properties[property[1]] = value;
                }
                return parsed;
            }
            return typeof body === 'string' ? JSON.parse(body) : null;
        } catch (_) {
            return null;
        }
    }

    getTrackedCartItems(items) {
        return (items || []).filter(item => {
            const props = item.properties || {};
            return (!props._item_type || props._item_type === 'main')
                && props._cde_managed !== '1' && !props._shipping_speed
                && Number(item.quantity) > 0;
        });
    }

    getCartItemIdentity(item) {
        return JSON.stringify([
            String(item.variant_id || item.id || ''),
            String(item.selling_plan || item.selling_plan_allocation?.selling_plan?.id || ''),
            Object.entries(item.properties || {}).filter(([, value]) => value != null && value !== '')
                .map(([key, value]) => [key, String(value)]).sort(([a], [b]) => a.localeCompare(b))
        ]);
    }

    getDataFromRequest(cartItems, request) {
        const parsed = this.parseCartRequestBody(request?.body);
        if (!parsed) return cartItems;
        const requestedItems = Array.isArray(parsed.items) ? parsed.items : [parsed];
        // Shopify returns the line's new total quantity. Report only what this request added,
        // matching each variant/properties pair so accessories cannot supply the main quantity.
        const remaining = new Map();
        for (const item of requestedItems) {
            const quantity = Number(item.quantity ?? 1);
            if (!Number.isInteger(quantity) || quantity <= 0) continue;
            const key = this.getCartItemIdentity(item);
            remaining.set(key, (remaining.get(key) || 0) + quantity);
        }
        return cartItems.map(item => {
            const key = this.getCartItemIdentity(item);
            const quantity = Math.min(Number(item.quantity) || 0, remaining.get(key) || 0);
            remaining.set(key, (remaining.get(key) || 0) - quantity);
            return { ...item, quantity };
        });
    }
}

new gTagEvents();
